import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { fetchFundUsageDetails, updateFundUsageDetails, updateFundUsageOrder } from '../../../../store/startup/funding/fundingSlice';
import {
    Box,
    Typography,
    IconButton,
    Paper,
    Container,
    Button,
    TextField,
    Tooltip,
    useTheme,
    alpha,
    Grid,
    Alert,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Modal,
    Divider
} from '@mui/material';
import { HelpOutline, Add, Delete, ArrowUpward, ArrowDownward } from '@mui/icons-material';

const FundUsage = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { startupId } = useParams();
    const { fundUsageDetails, loading } = useSelector(state => state.funding);
    const [fundUsages, setFundUsages] = useState([]);
    const [errors, setErrors] = useState({});
    const [openModal, setOpenModal] = useState(false);
    const [newFundUsage, setNewFundUsage] = useState({
        description: '',
        usageStartDate: '',
        usageEndDate: '',
        amount: ''
    });

    useEffect(() => {
        dispatch(fetchFundUsageDetails(startupId));
    }, [dispatch, startupId]);

    useEffect(() => {
        if (fundUsageDetails?.length > 0) {
            const formattedUsages = fundUsageDetails.map((usage, index) => ({
                fundUsageDetailId: usage.fundUsageDetailId,
                description: usage.description,
                usageStartDate: new Date(usage.usageStartDate * 1000).toISOString().split('T')[0],
                usageEndDate: new Date(usage.usageEndDate * 1000).toISOString().split('T')[0],
                amount: usage.amount,
                order: index
            }));
            setFundUsages(formattedUsages);
        } else {
            setFundUsages([{
                fundUsageDetailId: '',
                description: '',
                usageStartDate: '',
                usageEndDate: '',
                amount: '',
                order: 0
            }]);
        }
    }, [fundUsageDetails]);

    const formatDateForAPI = (dateString) => {
        const date = new Date(dateString);
        return Math.floor(date.getTime() / 1000);
    };

    const validateForm = (usage) => {
        const newErrors = {};

        if (!usage.description?.trim()) {
            newErrors.description = 'Açıklama alanı zorunludur';
        }

        if (!usage.usageStartDate) {
            newErrors.usageStartDate = 'Başlangıç tarihi zorunludur';
        }

        if (!usage.usageEndDate) {
            newErrors.usageEndDate = 'Bitiş tarihi zorunludur';
        }

        if (!usage.amount || usage.amount <= 0) {
            newErrors.amount = 'Geçerli bir tutar giriniz';
        }

        if (usage.usageStartDate && usage.usageEndDate) {
            const startDate = new Date(usage.usageStartDate);
            const endDate = new Date(usage.usageEndDate);

            if (startDate > endDate) {
                newErrors.usageEndDate = 'Bitiş tarihi başlangıç tarihinden önce olamaz';
            }
        }

        return newErrors;
    };

    const handleChange = (index, field, value) => {
        if (index === fundUsages.length - 1) {
            setErrors({});
        }

        const newFundUsages = [...fundUsages];
        newFundUsages[index] = {
            ...newFundUsages[index],
            [field]: value
        };
        setFundUsages(newFundUsages);
    };

    const handleSave = async (index) => {
        if (index === fundUsages.length - 1) {
            const usage = fundUsages[index];
            const validationErrors = validateForm(usage);

            if (Object.keys(validationErrors).length > 0) {
                setErrors(validationErrors);
                return;
            }
        }

        try {
            const usage = fundUsages[index];
            await dispatch(updateFundUsageDetails({
                startupId,
                description: usage.description,
                usageStartDate: formatDateForAPI(usage.usageStartDate),
                usageEndDate: formatDateForAPI(usage.usageEndDate),
                amount: parseInt(usage.amount, 10)
            })).unwrap();
        } catch (error) {
            console.error('Failed to update fund usage:', error);
            if (error.errors) {
                const apiErrors = error.errors.reduce((acc, err) => ({
                    ...acc,
                    [err.path]: err.msg
                }), {});
                setErrors(apiErrors);
            }
        }
    };

    const addNewFundUsage = () => {
        setFundUsages([...fundUsages, {
            description: '',
            usageStartDate: '',
            usageEndDate: '',
            amount: ''
        }]);
        setErrors({});
    };

    const handleOpenModal = () => {
        setOpenModal(true);
        setErrors({});
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setNewFundUsage({
            description: '',
            usageStartDate: '',
            usageEndDate: '',
            amount: ''
        });
        setErrors({});
    };

    const handleModalInputChange = (field, value) => {
        setNewFundUsage(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleModalSave = async () => {
        const validationErrors = validateForm(newFundUsage);

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        try {
            await dispatch(updateFundUsageDetails({
                startupId,
                description: newFundUsage.description,
                usageStartDate: formatDateForAPI(newFundUsage.usageStartDate),
                usageEndDate: formatDateForAPI(newFundUsage.usageEndDate),
                amount: parseInt(newFundUsage.amount, 10)
            })).unwrap();
            handleCloseModal();
        } catch (error) {
            console.error('Failed to add fund usage:', error);
            if (error.errors) {
                const apiErrors = error.errors.reduce((acc, err) => ({
                    ...acc,
                    [err.path]: err.msg
                }), {});
                setErrors(apiErrors);
            }
        }
    };

    const handleDelete = async (index) => {
        try {
            const usage = fundUsages[index];
            // Remove the item from local state
            const newFundUsages = fundUsages.filter((_, idx) => idx !== index);
            setFundUsages(newFundUsages);
        } catch (error) {
            console.error('Failed to delete fund usage:', error);
        }
    };

    const moveFundUsage = async (index, direction) => {
        const newFundUsages = [...fundUsages];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex < 0 || targetIndex >= newFundUsages.length) return;

        const temp = newFundUsages[index];
        newFundUsages[index] = newFundUsages[targetIndex];
        newFundUsages[targetIndex] = temp;

        newFundUsages[index].order = index;
        newFundUsages[targetIndex].order = targetIndex;

        for (let i = 0; i < newFundUsages.length; i++) {
            newFundUsages[i].usageStartDate = formatDateForAPI(newFundUsages[i].usageStartDate);
            newFundUsages[i].usageEndDate = formatDateForAPI(newFundUsages[i].usageEndDate);
        }

        await dispatch(updateFundUsageOrder({ startupId, fundUsages: newFundUsages }));
        dispatch(fetchFundUsageDetails(startupId));
    };

    const handleOrderUpdate = async () => {
        try {
            await dispatch(updateFundUsageOrder({
                startupId,
                fundUsages
            })).unwrap();
        } catch (error) {
            console.error('Failed to update fund usage order:', error);
        }
    };

    return (
        <div>
            <Divider sx={{ marginBottom: 3, marginTop: 4 }} />
            <Box sx={{ display: 'flex', width: '100%' }}>
                <Box sx={{ flexGrow: 1, width: '100%' }}>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        Topladığın Fonu Nerelerde Kullanacaksın?
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'right', justifyContent: 'right', width: '20%', height: '1%' }}>
                    <Button
                        startIcon={<Add />}
                        color="primary"
                        onClick={handleOpenModal}
                        variant="contained"
                        size="small"
                    >
                        Yeni Ekle
                    </Button>
                </Box>
                <Divider sx={{ marginBottom: 3 }} />
            </Box>

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Açıklama</TableCell>
                            <TableCell>Başlangıç Tarihi</TableCell>
                            <TableCell>Bitiş Tarihi</TableCell>
                            <TableCell>Tutar</TableCell>
                            <TableCell align="right">İşlemler</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fundUsages.map((usage, index) => (
                            <TableRow key={index}>
                                <TableCell>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        disabled
                                        value={usage.description}
                                        onChange={(e) => handleChange(index, 'description', e.target.value)}
                                        error={!!errors.description}
                                        helperText={errors.description}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        type="date"
                                        disabled
                                        value={usage.usageStartDate}
                                        onChange={(e) => handleChange(index, 'usageStartDate', e.target.value)}
                                        error={!!errors.usageStartDate}
                                        helperText={errors.usageStartDate}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        type="date"
                                        disabled
                                        value={usage.usageEndDate}
                                        onChange={(e) => handleChange(index, 'usageEndDate', e.target.value)}
                                        error={!!errors.usageEndDate}
                                        helperText={errors.usageEndDate}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        type="number"
                                        disabled
                                        value={usage.amount}
                                        onChange={(e) => handleChange(index, 'amount', e.target.value)}
                                        error={!!errors.amount}
                                        helperText={errors.amount}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton
                                        onClick={() => moveFundUsage(index, 'up')}
                                        disabled={index === 0}
                                    >
                                        <ArrowUpward />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => moveFundUsage(index, 'down')}
                                        disabled={index === fundUsages.length - 1}
                                    >
                                        <ArrowDownward />
                                    </IconButton>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={() => handleDelete(index)}
                                        disabled={loading}
                                        size="small"
                                        startIcon={<Delete />}
                                        sx={{
                                            '&:hover': {
                                                bgcolor: theme.palette.error.dark,
                                            },
                                        }}
                                    >
                                        Sil
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Button
                variant="contained"
                onClick={handleOrderUpdate}
                disabled={loading}
            >
                {loading ? 'Kaydediliyor...' : 'Sıralamayı Kaydet'}
            </Button>

            <Modal
                open={openModal}
                onClose={handleCloseModal}
                aria-labelledby="fund-usage-modal"
                aria-describedby="fund-usage-modal-description"
                sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
                <Box sx={{
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 1,
                }}>
                    <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
                        Yeni Fon Kullanımı Ekle
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Açıklama"
                                value={newFundUsage.description}
                                onChange={(e) => handleModalInputChange('description', e.target.value)}
                                error={!!errors.description}
                                helperText={errors.description}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Başlangıç Tarihi"
                                value={newFundUsage.usageStartDate}
                                onChange={(e) => handleModalInputChange('usageStartDate', e.target.value)}
                                error={!!errors.usageStartDate}
                                helperText={errors.usageStartDate}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Bitiş Tarihi"
                                value={newFundUsage.usageEndDate}
                                onChange={(e) => handleModalInputChange('usageEndDate', e.target.value)}
                                error={!!errors.usageEndDate}
                                helperText={errors.usageEndDate}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Tutar"
                                value={newFundUsage.amount}
                                onChange={(e) => handleModalInputChange('amount', e.target.value)}
                                error={!!errors.amount}
                                helperText={errors.amount}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={handleCloseModal}
                        >
                            İptal
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleModalSave}
                            disabled={loading}
                        >
                            {loading ? 'Kaydediliyor...' : 'Kaydet'}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </div>
    );
};

export default FundUsage;